import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Shield, PenLine, FileDown, Clock, Sparkles, LayoutList } from "lucide-react";

const LOGO_URL =
    "https://customer-assets-wrfwihn1.emergentagent.net/job_authority-scripts/artifacts/p0dveaq9_UnCloned%C2%AE%20Logo%20with%20Registered%20R%20%28Various%20Colors%29-01.webp";

const Landing = () => {
    const { user } = useAuth();
    const dashPath = user?.role === "admin" ? "/admin" : "/dashboard";

    return (
        <div className="min-h-screen bg-white text-un-onyx" data-testid="landing-page">
            {/* Hero */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-24">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="un-h1 text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-6" data-testid="landing-headline">
                        UnCloned® Short Form Video <span className="text-un-berry">Strategist</span>
                    </h1>
                    <p className="text-base md:text-xl lg:text-2xl max-w-3xl mx-auto text-un-onyx/75 leading-snug mb-10" data-testid="landing-subtitle">
                        Your Strategic Sidekick For Creating Authority-Building Short-Form Video Scripts.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {user ? (
                            <Link
                                to={dashPath}
                                className="un-btn-primary text-base inline-flex items-center gap-2"
                                data-testid="cta-dashboard"
                            >
                                Go To Your Workspace <ArrowRight className="w-4 h-4" />
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/signup"
                                    className="un-btn-primary text-base inline-flex items-center gap-2"
                                    data-testid="cta-redeem"
                                >
                                    Redeem An Activation Code <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/login" className="un-btn-outline text-base" data-testid="cta-login">
                                    Client Login
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* The Script Creator — dedicated section */}
            <section className="border-t-2 border-un-onyx bg-un-water" data-testid="workspace-section">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                    {/* Centered headline block matching How It Works pattern */}
                    <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20">
                        <div className="un-label text-un-grape mb-5 text-sm md:text-base">Why It Works</div>
                        <h2 className="un-h1 text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-none mb-6">
                            Attention Into <span className="text-un-berry">Action.</span>
                        </h2>
                        <p className="text-lg md:text-2xl text-un-onyx/70 max-w-2xl mx-auto leading-snug">
                            Four pillars built to turn views into real results.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-un-onyx" data-testid="workspace-card">
                        {/* Tile 01 — BERRY */}
                        <div className="bg-un-berry text-white p-8 md:p-10 border-b-2 lg:border-b-0 lg:border-r-2 border-un-onyx flex flex-col">
                            <div className="flex items-start justify-between mb-8">
                                <span className="un-mono text-xs opacity-80">01</span>
                                <PenLine className="w-6 h-6" />
                            </div>
                            <div className="un-h3 text-[1.75rem] md:text-[2rem] leading-[1.1] font-extrabold min-h-[3.75rem] md:min-h-[4.5rem] mb-3 break-words">
                                Zero Blank Pages
                            </div>
                            <p className="text-base md:text-lg leading-relaxed opacity-90">
                                We ask the questions that unlock your sharpest angles. You just answer. No staring at a cursor.
                            </p>
                        </div>
                        {/* Tile 02 — LIME */}
                        <div className="bg-un-lime text-un-onyx p-8 md:p-10 border-b-2 lg:border-b-0 lg:border-r-2 md:border-r-0 border-un-onyx flex flex-col">
                            <div className="flex items-start justify-between mb-8">
                                <span className="un-mono text-xs opacity-70">02</span>
                                <Sparkles className="w-6 h-6" />
                            </div>
                            <div className="un-h3 text-[1.75rem] md:text-[2rem] leading-[1.1] font-extrabold min-h-[3.75rem] md:min-h-[4.5rem] mb-3 break-words">
                                Three Scripts, Every Time
                            </div>
                            <p className="text-base md:text-lg leading-relaxed opacity-80">
                                Never settle for one direction. Every session delivers three distinct scripts so you can pick the hook that lands.
                            </p>
                        </div>
                        {/* Tile 03 — GRAPE */}
                        <div className="bg-un-grape text-white p-8 md:p-10 border-b-2 md:border-b-0 md:border-r-2 lg:border-b-0 border-un-onyx flex flex-col">
                            <div className="flex items-start justify-between mb-8">
                                <span className="un-mono text-xs opacity-80">03</span>
                                <LayoutList className="w-6 h-6" />
                            </div>
                            <div className="un-h3 text-[1.75rem] md:text-[2rem] leading-[1.1] font-extrabold min-h-[3.75rem] md:min-h-[4.5rem] mb-3 break-words">
                                Frameworks That Sell
                            </div>
                            <p className="text-base md:text-lg leading-relaxed opacity-90">
                                Every script is labeled by section: Hook, Authority Core, Proof, and Call-To-Action. Open, film, done.
                            </p>
                        </div>
                        {/* Tile 04 — PUMPKIN */}
                        <div className="bg-un-pumpkin text-un-onyx p-8 md:p-10 flex flex-col">
                            <div className="flex items-start justify-between mb-8">
                                <span className="un-mono text-xs opacity-70">04</span>
                                <FileDown className="w-6 h-6" />
                            </div>
                            <div className="un-h3 text-[1.75rem] md:text-[2rem] leading-[1.1] font-extrabold min-h-[3.75rem] md:min-h-[4.5rem] mb-3 break-words">
                                Copy, Post, Repeat
                            </div>
                            <p className="text-base md:text-lg leading-relaxed opacity-80">
                                Copy straight into your teleprompter or export a branded PDF. Your scripts, your brand, your workflow.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="border-t-2 border-un-onyx bg-un-onyx text-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28">
                    {/* Centered headline block */}
                    <div className="max-w-4xl mx-auto text-center mb-16 md:mb-20">
                        <div className="un-label text-un-lime mb-5 text-sm md:text-base">The Process</div>
                        <h2 className="un-h1 text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-none mb-6">
                            How It <span className="text-un-berry">Works.</span>
                        </h2>
                        <p className="text-lg md:text-2xl text-white/70 max-w-2xl mx-auto leading-snug">
                            From blank page to camera-ready script in four steps.
                        </p>
                    </div>

                    {/* Steps — no outer border, thin dividers between steps */}
                    <div className="max-w-4xl mx-auto">
                        {[
                            {
                                n: "01",
                                accent: "text-un-berry",
                                icon: Shield,
                                title: "Claim Your Access",
                                copy: "Redeem your invite code and step into your private strategist. No noise, no learning curve.",
                            },
                            {
                                n: "02",
                                accent: "text-un-lime",
                                icon: PenLine,
                                title: "Answer A Few Questions",
                                copy: "We ask what actually matters: your offer, your buyer, and your edge. Your answers become the script's DNA.",
                            },
                            {
                                n: "03",
                                accent: "text-un-water",
                                icon: Clock,
                                title: "Get Three Scripts Instantly",
                                copy: "Three complete, camera-ready scripts show up in seconds. Different angles, same authority.",
                            },
                            {
                                n: "04",
                                accent: "text-un-pumpkin",
                                icon: FileDown,
                                title: "Film. Post. Convert.",
                                copy: "Copy the winner or download a branded PDF. Then film, post, and let the script do its job.",
                            },
                        ].map((s, i, arr) => {
                            const I = s.icon;
                            return (
                                <div
                                    key={s.n}
                                    className={`grid grid-cols-12 gap-4 md:gap-6 items-center py-8 md:py-12 ${
                                        i < arr.length - 1 ? "border-b border-white/20" : ""
                                    }`}
                                    data-testid={`step-${s.n}`}
                                >
                                    <div className="col-span-3 md:col-span-2 flex flex-col items-start gap-2">
                                        <div className={`${s.accent} un-h1 text-4xl md:text-7xl leading-none`}>
                                            {s.n}
                                        </div>
                                        <I className={`w-6 h-6 md:hidden ${s.accent}`} />
                                    </div>
                                    <div className="col-span-9 md:col-span-8 text-left md:text-center">
                                        <div className="un-label text-white/40 mb-1 md:mb-2 text-[0.65rem] md:text-xs">Step {s.n}</div>
                                        <div className="un-h2 text-xl md:text-3xl mb-2 leading-tight">
                                            {s.title}
                                        </div>
                                        <p className="text-sm md:text-base text-white/70 leading-snug max-w-md md:mx-auto">
                                            {s.copy}
                                        </p>
                                    </div>
                                    <div className="hidden md:flex col-span-2 justify-end">
                                        <I className={`w-10 h-10 lg:w-12 lg:h-12 ${s.accent}`} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Landing;
